import React, { useCallback, useEffect, useMemo, useState, } from 'react';

import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import {
  CannedAnswerAddForm,
  CannedAnswerAddInitialValues,
  CannedAnswerComponents,
  CannedAnswerAdditionalParams,
  CannedAnswerFormOptions,
  onCannedAnswerFormSaved,
  ICannedAnswerFormOptionsProps,
  AlertError,
  queryParam,
} from '@Utils';
import { AtlasForm, IFormOptions, } from 'atlas-form';
import { CrudEnum, ISharedCreateCannedAnswer, PermissionEnum, } from 'atlas-shared';
import { createCannedAnswer, fetchCannedAnswer, getCannedAnswerCreateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withAdd, IWithAddProps, } from '@Hocs/with.add';
import { useAbilities, useAuth, useOrganizations, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithAddProps<ISharedCreateCannedAnswer> {}

const CannedAnswerFormAdd = ({ values, setValues, save, formSchema, schemaLoading, saving, initialValues, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const organizations = useOrganizations();
  const abilities = useAbilities();
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const dispatch = useAppDispatch();

  const [cloned, setCloned, ] = useState<Partial<ISharedCreateCannedAnswer>>();

  const param_props: ICannedAnswerFormOptionsProps = {
    t,
    is_new: true,
    abilities,
    organizations: organizations.organizations,
  };
  const formLayout = useMemo(() => CannedAnswerAddForm(values, param_props, t), [values, organizations, t, abilities, ]);
  const options: IFormOptions = useMemo(() => CannedAnswerFormOptions(values || CannedAnswerAddInitialValues, param_props, t), [values, organizations, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedCreateCannedAnswer, form) => {
    setValues(values);
    onChange?.(values, changedValue, form);
  }, [setValues, ]);
  const onFinish = useCallback((values: ISharedCreateCannedAnswer) => save(() => createCannedAnswer(values).then(
    res => onSaved ? onSaved(res, navigate) : onCannedAnswerFormSaved(t, 'add', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [save, navigate, t, ]);
  
  useEffect(() => {
    const id = queryParam('clone');

    if (id)
      fetchCannedAnswer(+id)
        .then(({
          organization_id,
          title,
          channel,
          type,
          body,
          global,
          description,
        }) => {
          setCloned({
            organization_id,
            title,
            channel,
            type,
            body,
            global,
            description,
          });
        });
    else
      setCloned({});
  }, []);
  
  if (schemaLoading || !cloned)
    return <AppSpinner />;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !values || abilities.can(values.organization_id, CrudEnum.Insert, PermissionEnum.CannedAnswer, values)}
    >
      <AtlasForm
        dispatch={dispatch}
        formLayout={formLayout}
        form={formSchema}
        options={options}
        components={CannedAnswerComponents}
        additionalParams={CannedAnswerAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={{ ...(cloned || {}), ...CannedAnswerAddInitialValues, ...initialValues, }}
        saving={saving}
        title={title ? t(title) : `${t('ADD')} ${t('CANNED_ANSWER')}`}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const CannedAnswerFormAddView = React.memo(withAdd(CannedAnswerFormAdd, getCannedAnswerCreateSchema, CannedAnswerAddInitialValues));
